import React, { useState, useEffect, useCallback } from 'react'
import styles from './MainButton.module.css'

export interface MainButtonProps {
	text: string
	icon?: string | undefined
	color?: string
	textColor?: string
	isActive?: boolean
	isVisible?: boolean
	isLoading?: boolean
	onClick?: () => void
	className?: string
	height?: number
}

export const MainButton: React.FC<MainButtonProps> = ({
	text,
	icon,
	color = '#83B63A',
	textColor = '#ffffff',
	isActive = true,
	isVisible = true,
	isLoading = false,
	onClick,
	className,
	height = 48,
}) => {
	const [isEnabled, setIsEnabled] = useState(isActive)
	const [isShown, setIsShown] = useState(isVisible)
	const [isProgress, setIsProgress] = useState(isLoading)
	const [buttonText, setButtonText] = useState(text)
	const [buttonColor, setButtonColor] = useState(color)
	const [buttonTextColor, setButtonTextColor] = useState(textColor)

	useEffect(() => {
		setIsEnabled(isActive)
	}, [isActive])

	useEffect(() => {
		setIsShown(isVisible)
	}, [isVisible])

	useEffect(() => {
		setIsProgress(isLoading)
	}, [isLoading])

	useEffect(() => {
		setButtonText(text)
	}, [text])

	useEffect(() => {
		setButtonColor(color)
	}, [color])

	useEffect(() => {
		setButtonTextColor(textColor)
	}, [textColor])

	const handleClick = useCallback(() => {
		if (isEnabled && !isProgress && onClick) {
			onClick()
		}
	}, [isEnabled, isProgress, onClick])

	// Определяем стиль кнопки
	const buttonStyle = {
		backgroundColor: buttonColor,
		color: buttonTextColor,
		opacity: isEnabled ? 1 : 0.7,
		cursor: isEnabled && !isProgress ? 'pointer' : 'default',
		height: `${height}px`,
	}

	if (!isShown) {
		return null
	}

	return (
		<div className={`${styles.mainButtonContainer} ${className || ''}`}>
			<button className={styles.mainButton} style={buttonStyle} onClick={handleClick} disabled={!isEnabled || isProgress}>
				{isProgress && (
					<div className={styles.loaderContainer}>
						<div className={styles.loader}></div>
					</div>
				)}
				<span className={isProgress ? styles.textWithLoader : ''} style={{ color: buttonTextColor }}>
					{buttonText}
					{icon && !isProgress && <img src={icon} alt='icon' className={styles.buttonIcon} />}
				</span>
			</button>
		</div>
	)
}

// API в стиле Telegram WebApp.MainButton
export class MainButtonController {
	private updateParams: (params: Partial<MainButtonProps>) => void

	constructor(updateParams: (params: Partial<MainButtonProps>) => void) {
		this.updateParams = updateParams
	}

	setText(text: string): void {
		this.updateParams({ text })
	}

	setColor(color: string): void {
		this.updateParams({ color })
	}

	setTextColor(textColor: string): void {
		this.updateParams({ textColor })
	}

	setParams(params: { text?: string; color?: string; text_color?: string; is_active?: boolean; is_visible?: boolean; height?: number }): void {
		const convertedParams: Partial<MainButtonProps> = {}

		if (params.text !== undefined) convertedParams.text = params.text
		if (params.color !== undefined) convertedParams.color = params.color
		if (params.text_color !== undefined) convertedParams.textColor = params.text_color
		if (params.is_active !== undefined) convertedParams.isActive = params.is_active
		if (params.is_visible !== undefined) convertedParams.isVisible = params.is_visible
		if (params.height !== undefined) convertedParams.height = params.height

		this.updateParams(convertedParams)
	}

	enable(): void {
		this.updateParams({ isActive: true })
	}

	disable(): void {
		this.updateParams({ isActive: false })
	}

	show(): void {
		this.updateParams({ isVisible: true })
	}

	hide(): void {
		this.updateParams({ isVisible: false })
	}

	showProgress(show: boolean): void {
		this.updateParams({ isLoading: show })
	}

	hideProgress(): void {
		this.updateParams({ isLoading: false })
	}

	onClick(callback: () => void): void {
		this.updateParams({ onClick: callback })
	}

	offClick(): void {
		this.updateParams({ onClick: undefined })
	}

	setHeight(height: number): void {
		this.updateParams({ height })
	}

	setIcon(icon?: string): void {
		this.updateParams({ icon })
	}
}

// Хук для использования MainButton с API в стиле Telegram
export const useMainButton = (initialParams: Partial<MainButtonProps> = {}): [React.FC, MainButtonController] => {
	const [params, setParams] = useState<MainButtonProps>({
		text: initialParams.text || 'Продолжить',
		color: initialParams.color || '#83B63A',
		textColor: initialParams.textColor || '#ffffff',
		isActive: initialParams.isActive !== undefined ? initialParams.isActive : true,
		isVisible: initialParams.isVisible !== undefined ? initialParams.isVisible : true,
		isLoading: initialParams.isLoading || false,
		onClick: initialParams.onClick,
		height: initialParams.height || 48,
	})

	const updateParams = useCallback((newParams: Partial<MainButtonProps>) => {
		setParams(prev => ({ ...prev, ...newParams }))
	}, [])

	const controller = new MainButtonController(updateParams)

	const ButtonComponent: React.FC = () => <MainButton {...params} />

	return [ButtonComponent, controller]
}
